// Direct Manager Discrepancy Audit API client (ADR-010 Phase 6).

import {
  authenticatedApi,
  ApiError,
  API_BASE_URL,
} from './apiUtils';

const BASE = `${API_BASE_URL}/reports/direct-manager-discrepancies`;

const buildQuery = (params = {}) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return;
      }
      sp.append(key, value.join(','));
    } else {
      sp.append(key, value);
    }
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
};

export const directManagerDiscrepanciesApi = {
  /**
   * List discrepancy runs with cursor pagination.
   * @param {Object} options
   * @param {string|string[]} [options.sourceSystem]
   * @param {string|string[]} [options.status]
   * @param {number} [options.limit=50]
   * @param {string} [options.cursor]
   * @returns {Promise<{items: Array, pageInfo: {hasNextPage: boolean, endCursor: string|null}}>}
   */
  listRuns: async ({ sourceSystem, status, limit = 50, cursor } = {}) => {
    try {
      const qs = buildQuery({ sourceSystem, status, limit, cursor });
      return await authenticatedApi.get(`${BASE}/runs${qs}`);
    } catch (error) {
      throw new ApiError('Failed to list discrepancy runs', error.status || 500, error.data);
    }
  },

  /**
   * Get a single run by id.
   */
  getRun: async (runId) => {
    try {
      return await authenticatedApi.get(`${BASE}/runs/${runId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch discrepancy run', error.status || 500, error.data);
    }
  },

  /**
   * List discrepancies for a run with cursor pagination.
   * @param {Object} options
   * @param {string} options.runId - Required.
   * @param {boolean|string} [options.match] - true / false / undefined
   * @param {string|string[]} [options.mismatchReason]
   * @param {string|string[]} [options.mappingStatus]
   * @param {string|string[]} [options.radarOnlyReason] - ADR-130
   * @param {string} [options.employeeId] - filter to a single consultant (UI uses EmployeeSearch).
   * @param {number} [options.limit=50]
   * @param {string} [options.cursor]
   */
  listDiscrepancies: async ({
    runId,
    match,
    mismatchReason,
    mappingStatus,
    radarOnlyReason,
    employeeId,
    limit = 50,
    cursor,
  } = {}) => {
    if (!runId) {
      throw new ApiError('runId is required to list discrepancies', 400);
    }
    try {
      const qs = buildQuery({
        runId, match, mismatchReason, mappingStatus, radarOnlyReason, employeeId, limit, cursor,
      });
      return await authenticatedApi.get(`${BASE}${qs}`);
    } catch (error) {
      throw new ApiError('Failed to list discrepancies', error.status || 500, error.data);
    }
  },

  /**
   * Trigger a new discrepancy run on demand. Admin-only — gated by CASL on the backend.
   *
   * Calls the dedicated trigger-now endpoint that proxies to radar-scheduler so the
   * on-demand path runs the same create-then-fill orchestration as the cron-driven
   * daily run. radar-scheduler handles the create-run + fetch-from-PowerBI + map +
   * snapshot-back chain. radar-api responds 202 once the scheduler acknowledges the
   * trigger; the new run will appear in the list shortly with status='running' and
   * transition to 'success' when the snapshot lands.
   *
   * @param {Object} options
   * @param {string} options.sourceSystem - Required. Defaults to ENOVA at the call site.
   */
  triggerRun: async ({ sourceSystem }) => {
    try {
      return await authenticatedApi.post(`${BASE}/runs/trigger-now`, { sourceSystem });
    } catch (error) {
      throw new ApiError('Failed to trigger discrepancy run', error.status || 500, error.data);
    }
  },

  /**
   * ADR-131: the upstream feed record behind one discrepancy row.
   * @param {string} discrepancyId
   * @returns {Promise<{discrepancyId, sourceSystem, schemaVersion, fetchedAt, payload}>}
   */
  getSourceRecord: async (discrepancyId) => {
    try {
      return await authenticatedApi.get(`${BASE}/discrepancies/${discrepancyId}/source-record`);
    } catch (error) {
      throw new ApiError('Failed to fetch the source record', error.status || 500, error.data);
    }
  },

};

export default directManagerDiscrepanciesApi;
